import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { getIsProd } from "../config";

export interface OfferChecklistState {
    data: {
        PROTECTION: boolean;
        NON_PARTNER_DEVICES: boolean;
        TECH_SUPPORT: boolean;
        FUTURE_DEVICES: boolean;
        IN_HOME_INSTALLATION: boolean;
        IN_STORE_SERVICES: boolean;
        ASK_SALE: boolean;
        BILLING: boolean;
        TRANSITION: boolean;
        COVERAGE: boolean;
        RAPPORT: boolean;
        DEVICE_CUE: boolean;
        ENGAGEMENT_QUESTION: boolean;
        DISCOVERY_QUESTION: boolean;
    };
}

export type OfferChecklistKey = keyof OfferChecklistState["data"];

export interface OfferChecklistStore {
    offerChecklistsBySession: Record<string, Record<string, OfferChecklistState>>;
    getData: (sessionId: string, productSku: string) => OfferChecklistState["data"];
    setData: (sessionId: string, productSku: string, newData: Partial<OfferChecklistState["data"]>) => void;
    resetData: (sessionId: string, productSku?: string) => void;
}

const defaultOfferChecklistState = {
    PROTECTION: false,
    NON_PARTNER_DEVICES: false,
    TECH_SUPPORT: false,
    FUTURE_DEVICES: false,
    IN_HOME_INSTALLATION: false,
    IN_STORE_SERVICES: false,
    ASK_SALE: false,
    BILLING: false,
    TRANSITION: false,
    COVERAGE: false,
    RAPPORT: false,
    DEVICE_CUE: false,
    ENGAGEMENT_QUESTION: false,
    DISCOVERY_QUESTION: false,
};

export const useOfferChecklistStore = create<OfferChecklistStore>()(
    persist(
        immer(
            devtools(
                (set, get) => ({
                    offerChecklistsBySession: {},
                    getData: (sessionId: string, productSku: string) =>
                        get().offerChecklistsBySession[sessionId]?.[productSku]?.data ?? defaultOfferChecklistState,
                    setData: (sessionId: string, productSku: string, newData: Partial<OfferChecklistState["data"]>) => {
                        set(
                            (state: OfferChecklistStore) => {
                                const existingData =
                                    state.offerChecklistsBySession[sessionId]?.[productSku]?.data ??
                                    defaultOfferChecklistState;
                                state.offerChecklistsBySession[sessionId] ??= {};
                                state.offerChecklistsBySession[sessionId][productSku] = {
                                    data: { ...existingData, ...newData },
                                };
                            },
                            false,
                            "setData",
                        );
                    },
                    resetData: (sessionId: string, productSku?: string) => {
                        set((state: OfferChecklistStore) => {
                            // Checked against `undefined`, not truthiness — an empty-string productSku is a
                            // valid (if degenerate) key, and treating it as "no productSku" would wipe the
                            // whole session instead of that one bucket.
                            if (productSku !== undefined) {
                                delete state.offerChecklistsBySession[sessionId]?.[productSku];
                            } else {
                                delete state.offerChecklistsBySession[sessionId];
                            }
                        });
                    },
                }),
                {
                    enabled: !getIsProd(),
                    store: "offer-checklist-store",
                    name: "workspace/sales-tools",
                },
            ),
        ),
        { name: "offer-checklist-store", skipHydration: true },
    ),
);
